// The design lock for every mark that is not a card: the buttons and the
// column marks. No button carries a word. Each is drawn in the same hand
// as the card glyphs (round strokes, filled nodes) and is meant to be
// guessed, never labelled; `meaning` is what a player should come to read
// in it. Drawn on a 24 × 24 grid; `.g-fill` parts are filled, the rest
// stroked.

export const ICON_IDS = [
  "title",
  "lattice",
  "undo",
  "restart",
  "rosetta",
  "book",
  "sound",
  "glimmer",
  "mirror",
  "tide",
  "introit",
  "anew",
  "ask",
] as const
export type IconId = (typeof ICON_IDS)[number]

export type IconConfig = { readonly svg: string; readonly meaning: string }

export const ICONS = {
  title: {
    svg: '<circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2.5" class="g-fill" />',
    meaning: "the ring you began at: back to the title",
  },
  lattice: {
    svg: '<circle cx="6" cy="6" r="1.6" class="g-fill" /><circle cx="12" cy="6" r="1.6" class="g-fill" /><circle cx="18" cy="6" r="1.6" class="g-fill" /><circle cx="6" cy="12" r="1.6" class="g-fill" /><circle cx="12" cy="12" r="2.4" /><circle cx="18" cy="12" r="1.6" class="g-fill" /><circle cx="6" cy="18" r="1.6" class="g-fill" /><circle cx="12" cy="18" r="1.6" class="g-fill" /><circle cx="18" cy="18" r="1.6" class="g-fill" />',
    meaning: "a tablet of nine points with you at the middle: the lattice of rites",
  },
  undo: {
    svg: '<path d="M8 7 L4 11 L8 15" /><path d="M4 11 H14 A6 6 0 0 1 14 23" transform="translate(0,-4)" />',
    meaning: "a path bending back: take back the last move",
  },
  restart: {
    svg: '<path d="M19 12 A7 7 0 1 1 15.5 5.9" /><path d="M15.5 2.5 V6.2 H19.2" />',
    meaning: "a full turn of the ring: deal the rite again from its start",
  },
  rosetta: {
    svg: '<rect x="4.5" y="2.5" width="15" height="19" rx="2.5" /><circle cx="12" cy="8.4" r="3.2" /><path d="M8.8 8.4 A3.2 3.2 0 0 1 15.2 8.4" class="g-bold" /><path d="M7 13.2 H17" /><path d="M7.6 19 L8.9 16.4 L10.2 19" /><circle cx="12" cy="17.8" r="1" class="g-fill" /><path d="M13.8 16.4 L15.1 19 L16.4 16.4" />',
    meaning: "one stone, two scripts: a glyph above, its plain marks below. Turn it to read every card in marks",
  },
  book: {
    svg: '<path d="M3 5.5 C6 4.6 9 4.6 12 6.2 C15 4.6 18 4.6 21 5.5 V19 C18 18.1 15 18.1 12 19.6 C9 18.1 6 18.1 3 19 Z" /><path d="M12 6.2 V19.6" /><circle cx="7.5" cy="11.5" r="2.1" /><path d="M5.4 11.5 A2.1 2.1 0 0 1 9.6 11.5" class="g-bold" /><path d="M14.6 10 H18.6 M14.6 13 H18.6 M14.6 16 H17" />',
    meaning: "an open book with a glyph on its page: the laws in riddles, and your Codex",
  },
  sound: {
    svg: '<path d="M3 12h3l3-7 3 14 3-10 2 3h4" />',
    meaning: "a struck bell's wave: sound on or off",
  },
  glimmer: {
    svg: '<circle cx="12" cy="12" r="2.6" class="g-fill" /><path d="M12 7 V2.6 M16.33 14.5 L20.14 16.7 M7.67 14.5 L3.86 16.7" /><circle cx="17.2" cy="7.8" r="0.9" class="g-fill" /><circle cx="6.8" cy="7.8" r="0.9" class="g-fill" /><circle cx="12" cy="18.4" r="0.9" class="g-fill" />',
    meaning: "a spark with three rays: light the cards that can move now",
  },
  mirror: {
    svg: '<path d="M3 12 L21 12"/><path d="M6 9 A6 6 0 0 1 18 9"/><path d="M6 15 A6 6 0 0 0 18 15"/><circle cx="12" cy="5" r="1.3" class="g-fill"/><circle cx="12" cy="19" r="1.3" class="g-fill"/>',
    meaning: "an arc and its reflection across still water: this column shows every card turned over",
  },
  tide: {
    svg: '<path d="M12 3 A9 9 0 1 1 3.6 15.2"/><path d="M3.6 15.2 L2.4 10.8 M3.6 15.2 L7.8 13.6"/><path d="M12 8 A4 4 0 1 1 8.4 13.8"/>',
    meaning: "a turning swell: this column turns itself over after every fold",
  },
  introit: {
    svg: '<circle cx="12" cy="12" r="8.5"/><path d="M10.2 8.4 L15.6 12 L10.2 15.6 Z"/>',
    meaning: "a ring with the way forward inside it: watch the rites play themselves",
  },
  anew: {
    svg: '<path d="M12 11 A2 2 0 0 1 16 11 A4 4 0 0 1 8 11 A6 6 0 0 1 20 11 A8 8 0 0 1 4 11"/><circle cx="12" cy="11" r="1.6" class="g-fill"/>',
    meaning: "a spiral wound out from a seed: begin anew from the first rite, forgetting every rite won",
  },
  ask: {
    svg: '<path d="M8.5 8.5 A3.5 3.5 0 1 1 13.75 11.53 Q12 12.4 12 14.6"/><circle cx="12" cy="18.6" r="1.3" class="g-fill"/>',
    meaning: "a hook over a point: are you sure? Press again to begin anew",
  },
} as const satisfies Record<IconId, IconConfig>
